import type { FlagValue } from '@anchorage/feature-flags';
import { useFeatureFlagsContext } from '@anchorage/feature-flags';
import { Text } from '@latitude/text';
import { useToast } from '@latitude/toast';
import React, { useMemo, useState } from 'react';

import { FlagHeader } from './components/FlagHeader';
import { FlagItem } from './components/FlagItem';
import { hasUnsavedChanges, inferType } from './utils/flagUtils';

export const FeatureFlagsList: React.FC = () => {
  const {
    useAllFlags,
    useAllOverrides,
    setOverride,
    clearOverrides,
    hasFinalValues,
  } = useFeatureFlagsContext();
  const { toast } = useToast();

  // Get all current flag values (follows localStorage -> LD -> Split -> defaults)
  const flagsInProvider = useAllFlags({ disableLocalOverrides: true });

  // Get current overrides
  const currentOverrides = useAllOverrides();

  // Local editing state for each non-boolean flag before saving
  const [editingFlags, setEditingFlags] = useState<Record<
    string,
    FlagValue
  > | null>(null);

  // Create a map of overrides for easy lookup
  const overridesMap = useMemo(() => {
    const map = new Map<string, (typeof currentOverrides)[0]>();
    currentOverrides.forEach((override) => {
      map.set(override.key, override);
    });
    return map;
  }, [currentOverrides]);

  // Merge provider flags with localStorage overrides for display
  const flagsToRender = useMemo(() => {
    const merged: Record<string, FlagValue> = { ...flagsInProvider };
    currentOverrides.forEach((override) => {
      if (override.enabled) {
        merged[override.key] = override.value;
      }
    });
    return merged;
  }, [flagsInProvider, currentOverrides]);

  const handleFlagChange = (key: string, value: FlagValue) => {
    setEditingFlags((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Save individual flag
  const handleSaveFlag = (key: string) => {
    if (editingFlags?.[key] === undefined) {
      return;
    }

    try {
      const value = editingFlags[key];
      const currentValue = flagsToRender[key];

      if (currentValue === undefined) {
        console.warn(`Flag "${key}" not found in flagsToRender`);
        return;
      }

      const type = inferType(currentValue);

      // Parse object values from strings if needed
      let finalValue = value;
      if (type === 'object' && typeof value === 'string') {
        try {
          finalValue = JSON.parse(value);
        } catch (error) {
          toast({
            variant: 'danger',
            title: 'Invalid JSON',
            description: `Cannot save "${key}": ${error instanceof Error ? error.message : String(error)}`,
          });
          return;
        }
      }

      const existingOverride = overridesMap.get(key);
      const defaultValue =
        existingOverride?.defaultValue ?? flagsInProvider[key];

      setOverride({
        key,
        value: finalValue,
        type,
        enabled: true,
        defaultValue,
      });

      // Clear editing state for this flag
      setEditingFlags((prev) => {
        if (!prev) {
          return null;
        }
        const newState = { ...prev };
        delete newState[key];
        return Object.keys(newState).length > 0 ? newState : null;
      });
    } catch (error) {
      console.error('Failed to save feature flag:', error);
    }
  };

  // Reset individual flag
  const handleResetFlag = (key: string) => {
    try {
      const override = overridesMap.get(key);
      if (override?.defaultValue !== undefined) {
        // Remove the override by setting it back to the default value
        setOverride({
          ...override,
          value: override.defaultValue,
          enabled: false,
        });
      }

      // Clear editing state for this flag
      setEditingFlags((prev) => {
        if (!prev) {
          return null;
        }
        const newState = { ...prev };
        delete newState[key];
        return Object.keys(newState).length > 0 ? newState : null;
      });
    } catch (error) {
      console.error('Failed to reset feature flag:', error);
    }
  };

  // Handle boolean toggle change - saves immediately
  const handleBooleanToggle = (key: string, value: boolean) => {
    try {
      const existingOverride = overridesMap.get(key);
      const defaultValue =
        existingOverride?.defaultValue ?? flagsInProvider[key];

      setOverride({
        key,
        value,
        type: 'boolean',
        enabled: true,
        defaultValue,
      });
    } catch (error) {
      console.error('Failed to save feature flag:', error);
    }
  };

  const handleResetAll = () => {
    try {
      clearOverrides();
      setEditingFlags(null);
    } catch (error) {
      console.error('Failed to reset feature flags:', error);
    }
  };

  // Check if there are overrides to reset
  const hasOverrides = currentOverrides.length > 0;

  if (!hasFinalValues) {
    return (
      <div className="space-y-4">
        <Text variant="bodyRegular" color="muted">
          Loading feature flags...
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FlagHeader hasOverrides={hasOverrides} onResetAll={handleResetAll} />

      <div className="space-y-4">
        {Object.entries(flagsToRender).map(([key, currentValue]) => {
          const override = overridesMap.get(key);
          const isOverridden = override !== undefined;
          const defaultValue = override?.defaultValue ?? flagsInProvider[key];
          const displayValue = editingFlags?.[key] ?? currentValue;

          return (
            <FlagItem
              key={key}
              flagKey={key}
              currentValue={currentValue}
              displayValue={displayValue}
              isOverridden={isOverridden}
              defaultValue={defaultValue}
              hasUnsavedChanges={hasUnsavedChanges(
                editingFlags,
                key,
                currentValue,
              )}
              onChange={handleFlagChange}
              onReset={handleResetFlag}
              onSave={handleSaveFlag}
              onBooleanToggle={handleBooleanToggle}
            />
          );
        })}
      </div>
    </div>
  );
};
